import {
  batchCumulateByUid,
  batchCumulateForGuest,
  batchWelfareByUid,
  batchWelfareForGuest,
  receivePrize,
  userShareTask,
} from "@/api/configPage.ts";
import { normalizeGuestStairsStatusMap } from "@/hooks/useConfigPage/access";
import FloatingButton from "@/components/FloatButton";
import KingKongArea from "@/components/KingKongArea";
import LazyRender from "@/components/LazyRender";
import ImgSwiper from "@/components/swipers/ImgSwiper";
import {
  goToPageV2,
  hideLoading,
  NativeByUrlList,
  showLoading,
} from "@/utils/common";
import { trackGdp } from "@/utils/gio-report";
import { getChannel } from "@/utils/tools";
import { Toast } from "@xp-design/mobile";
import { forEach, map } from "lodash-es";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import HelpDialog from "./components/helpDialog";
import Lottery from "./components/lottery";
import PrizeDialog from "./components/PrizeDialog";
import PrizeSuccessPopup, {
  PrizeSuccessPopupData,
  PrizeSuccessPopupRef,
} from "./components/PrizeSuccessPopup";
import Ranking from "./components/ranking";
import Stairs from "./components/stairs";
import "./index.less";
import { enhancedSafeChainFind, parseJson } from "./utils";

import { useConfigPage } from "@/hooks/useConfigPage/useConfigPage";
import { Module, PageDataConfig } from "@/types/config-page";
import { processOssImage } from "@/utils/image";
import { appendWjxSojumpparm, getQueryHashString, isWjxUrl } from "@/utils/url";
import { getCachedUid } from "../../../api/common";
import DynamicStation from "./components/dynamicStation";
import FixedStation from "./components/fixedStation";
import BaseCountdown from "./components/baseCountdown";
import { useServerTimeSync } from "@/hooks/useServerTimeSync";

// 按需加载 VideoPlayer 组件
const VideoPlayer = lazy(
  () =>
    import(/* webpackChunkName: "VideoPlayer" */ "./components/VideoPlayer"),
);

// ==================== 类型定义 ====================

interface PageProps {
  pageCode?: number | string;
  versionCode?: number | string;
  previewType?: number | string;
  shareUid?: number | string;
  customCompRender?: (
    comp: object,
    index: number,
    originCompList?: object[],
  ) => React.ReactNode;
}

/** 奖品配置 */
interface PrizeConfig {
  /** 成功弹窗图片 */
  successPopupImage?: string;
  /** 跳转链接映射 */
  jumpUrlMap?: Record<string, string>;
}

// ==================== 组件 ====================

const Page = (props) => {
  useServerTimeSync();
  const fangPaiRef = useRef<any[]>([]);
  // 记录已消费过的配置数据引用，避免同一份数据被 effect 与 getDetail 重复消费
  const appliedDataRef = useRef<PageDataConfig | null>(null);

  const [pageVersionDto, setPageVersionDto] = useState<{
    backUrl: string;
    canShare: number;
    versionCode: string;
    pageCode: string;
  }>({
    backUrl: "",
    canShare: 1, // 默认可分享
    versionCode: "",
    pageCode: "",
  }); // 页面数据

  const [pageModuleDto, setPageModuleDto] = useState<Array<Module>>([]); // 组件列表
  const [prizeStatusMap, setPrizeStatusMap] = useState({}); // 领奖组件状态列表
  const [stairsStatusMap, setStairsStatusMap] = useState({}); // 阶梯组件状态列表
  const [isFirstUpdate, setIsFirstUpdate] = useState(false);
  const [showKeyIdx, setShowKeyIdx] = useState(-1);
  const prizeDialogRef = useRef<{ onOpen: (data: any) => void }>();
  const helpDialogRef = useRef<{ onOpen: () => void }>();
  const prizeSuccessPopupRef = useRef<PrizeSuccessPopupRef>(null);

  const setContentOverflow = (hidden: boolean) => {
    document.body.style.overflow = hidden ? "hidden" : "auto";
    const configPage = document.getElementById("configPage");
    if (configPage) {
      configPage.style.overflow = hidden ? "hidden" : "auto";
    }
  };
  const videoRefs = useRef<any[]>([]); // 用于存储所有视频的引用

  // 处理分享逻辑
  const shareUid = getQueryHashString("shareUid") || props?.shareUid;
  const { configPageQuery, ensureLogin, isGuest, navHeight, openedAsGuest } =
    useConfigPage();

  useEffect(() => {
    // 监听 hash 变化
    const handleHashChange = () => {
      console.log("hashchange---------------");
      window?.location?.reload();
    };

    window.addEventListener("hashchange", handleHashChange);

    // 节流后的 visibilitychange 事件处理函数
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("=====visibilitychange======", document.visibilityState);
        if (fangPaiRef.current.length > 0) {
          fangPaiRef.current.forEach((refEl) => {
            refEl?.getActivityInfo?.();
          });
        }
        getDetail();
      }
    }; // 100 毫秒内最多执行一次

    // 监听 visibilitychange 事件
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 首屏配置数据由 useQuery 首次请求提供，这里不再主动 getDetail，避免重复请求配置接口

    // 首次进入页面时设置 isFirstUpdate 为 true
    setIsFirstUpdate(true);

    // 清理事件监听器
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // 被分享人打开页面时调用
  const shareUserOpen = async () => {
    const shareUid = getQueryHashString("shareUid") || props?.shareUid;
    // 这里一定要从 url 取，被分享人打开时 url 上的参数才是有效的
    const pageCode = configPageQuery?.data?.pageCode;

    if (!shareUid || openedAsGuest) {
      return;
    }

    try {
      showLoading();
      const res = await userShareTask({
        pageCode,
        shareUid,
      });
      if (res.code === 200) {
        console.log("被分享人打开===", res.data);
        if (res.data.result == "0") {
          helpDialogRef.current?.onOpen?.();
        }
      } else {
        Toast.show({
          content: res.msg,
        });
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    if (shareUid && configPageQuery?.data?.pageCode) {
      shareUserOpen();
    }
  }, [shareUid, configPageQuery?.data?.pageCode]);

  useEffect(() => {
    if (isFirstUpdate && pageModuleDto.length > 0) {
      setIsFirstUpdate(false);
    }
  }, [pageModuleDto, isFirstUpdate]);

  /**
   * 消费页面配置数据（不发起配置请求）
   * 首屏数据由 useFetchConfigPage 的 useQuery 提供，刷新场景由 getDetail 提供，
   * 两者最终都走这里，避免重复请求配置接口
   */
  const applyPageData = (data?: PageDataConfig | null) => {
    try {
      appliedDataRef.current = data ?? null;

      // 更新页面数据
      setPageVersionDto(() => data || {});

      // 埋点
      trackGdp("P20189", "B001", {
        trackName: "充电_营销专题页_页面访问",
        origin: getChannel(),
        userType: "",
        pageId: data?.pageCode,
        versionId: data?.versionCode,
      });

      // 提前解析 JSON 数据
      const parsedModules = map(data?.moduleList, (e) => {
        e.moduleJsonData = parseJson(e.moduleJson);
        // 旧版接口部分数据放在modules里面，新接口把这些通用参数提取到外层，这里在前端透传实现对组件的消费做兼容
        e.pageCode = data?.pageCode;
        e.versionCode = data?.versionCode;
        return e;
      });

      // 更新组件列表
      setPageModuleDto(() => parsedModules);

      // 提取领奖和阶梯组件的 code 列表
      const valueCodeList: any[] = [];
      const stairsCodeList: any[] = [];

      parsedModules.forEach((e) => {
        if (e.moduleType === "busi-prize") {
          valueCodeList.push(e?.moduleJsonData?.base?.[0]?.val?.valCode);
        }
        if (e.moduleType === "busi-stairs") {
          stairsCodeList.push(e?.moduleJsonData?.base?.[0]?.val?.valCode);
        }
      });

      // 获取领奖组件状态
      if (valueCodeList.length) {
        getPrizeData(valueCodeList);
      }

      // 获取阶梯状态
      if (stairsCodeList.length) {
        getStairsStatus(stairsCodeList);
      }
    } catch (error) {
      console.error("解析页面配置失败:", error);
    }
  };

  // 首屏及 authMode 切换后的新数据，统一在这里消费，不额外发起配置请求
  useEffect(() => {
    const data = configPageQuery?.data;
    if (data && appliedDataRef.current !== data) {
      applyPageData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configPageQuery?.data]);

  /**
   * 主动刷新页面配置（回到前台、领奖成功、阶梯组件触发等场景）
   */
  const getDetail = async () => {
    try {
      showLoading();
      const res = await configPageQuery?.refetch();
      applyPageData(res?.data);
    } catch (error) {
      console.error("获取详情失败3:", error);
    } finally {
      hideLoading();
    }
  };

  // 获取领奖组件状态
  const getPrizeData = async (valCodeList) => {
    try {
      showLoading();
      const fetchPrizeStatus = isGuest
        ? batchWelfareForGuest
        : batchWelfareByUid;
      const res = await fetchPrizeStatus({
        activityCodeList: valCodeList,
      });
      if (res.code === 200) {
        console.log(res.data);
        setPrizeStatusMap(() => res.data.resultMap);
      }
    } catch (error: any) {
      console.error("获取领奖组件状态失败:", error);
    } finally {
      hideLoading();
    }
  };

  // 获取阶梯状态
  const getStairsStatus = async (valCodeList) => {
    try {
      showLoading();
      const fetchStairsStatus = isGuest
        ? batchCumulateForGuest
        : batchCumulateByUid;
      const res = await fetchStairsStatus({
        activityCodeList: valCodeList,
      });
      if (res.code === 200) {
        console.log(res.data);
        const resultMap = res.data?.resultMap || {};
        setStairsStatusMap(() =>
          isGuest ? normalizeGuestStairsStatusMap(resultMap) : resultMap,
        );
      }
    } catch (error: any) {
      console.error("获取阶梯状态失败:", error);
    } finally {
      hideLoading();
    }
  };

  // 领奖
  const receivePrizeData = async (
    valCode: string,
    item: any,
    prizeConfig?: PrizeConfig,
  ) => {
    trackGdp("P20189", "B004", {
      trackName: "充电_营销专题页_组件点击",
      origin: getChannel(),
      userType: "",
      versionId: pageVersionDto.versionCode,
      pageId: pageVersionDto.pageCode,
      ComponentCode: item.moduleId,
      type: item.moduleType,
      activityId: item.connectedActivitiesId,
      clickLocation: "领奖组件",
    });
    if (isGuest) {
      await ensureLogin?.();
      return;
    }
    try {
      showLoading();
      const res = await receivePrize({
        activityId: valCode,
        source: 1,
      });
      if (res.code === 200) {
        // 从传入的 prizeConfig 获取弹窗数据
        const successPopupImage = prizeConfig?.successPopupImage;
        const jumpUrlMap = prizeConfig?.jumpUrlMap;

        // 如果配置了成功弹窗图片，则展示弹窗；否则展示 toast
        if (successPopupImage) {
          const popupData: PrizeSuccessPopupData = {
            successPopupImage,
            jumpUrlMap,
          };
          prizeSuccessPopupRef.current?.open(popupData);
        } else {
          Toast.show({
            content: res?.data?.showContent || "领奖成功",
          });
        }

        getDetail();
      } else {
        Toast.show({
          content: res.msg,
        });
      }
    } catch (error: any) {
      console.error("领奖失败:", error);
    } finally {
      hideLoading();
    }
  };

  // 转驼峰
  const underscoreToCamelCase = (str) => {
    if (!str) {
      return;
    }
    return str
      .replace(/(-\w)/g, function (match) {
        return match[1].toUpperCase();
      })
      .replace(/^-/, "");
  };

  const onClickImg = async (item) => {
    const baseData = item.moduleJsonData.base[0];
    const clickAction = item.moduleJsonData.clickAction ?? "jumpLink";

    trackGdp("P20189", "B004", {
      trackName: "充电_营销专题页_组件点击",
      origin: getChannel(),
      userType: "",
      versionId: pageVersionDto.versionCode,
      pageId: pageVersionDto.pageCode,
      ComponentCode: item.moduleId,
      type: item.moduleType,
      activityId: item.connectedActivitiesId,
      clickLocation: "图片组件",
    });

    if (clickAction === "triggerLogin") {
      if (isGuest) {
        await ensureLogin?.();
      }
      return;
    }

    // 仅使用缓存 uid，匿名问卷链接不拼接用户信息且不触发登录
    const uid = getCachedUid();

    // 优先使用新的 jumpUrlMap 格式
    if (baseData.jumpUrlMap) {
      // 问卷星链接需要拼接 wjxparams=uid
      const patchedUrlMap = { ...baseData.jumpUrlMap };
      for (const key of Object.keys(patchedUrlMap)) {
        if (uid && isWjxUrl(patchedUrlMap[key])) {
          patchedUrlMap[key] = appendWjxSojumpparm(patchedUrlMap[key], uid);
        }
      }
      NativeByUrlList(patchedUrlMap, baseData.jumpParam);
    }
    // 兜底使用旧的 val 格式
    else if (baseData.val) {
      const jumpLink = uid
        ? appendWjxSojumpparm(baseData.val, uid) || baseData.val
        : baseData.val;
      if (jumpLink.includes(window.location.origin)) {
        window.location.href = jumpLink;
      } else {
        goToPageV2({ url: jumpLink });
      }
    }
  };

  //埋点-组件曝光
  const track = (item) => {
    console.log("track_click点击", new Date().getTime());
    trackGdp("P20189", "B003", {
      trackName: "充电_营销专题页_组件曝光",
      origin: getChannel(),
      userType: "",
      versionId: pageVersionDto.versionCode,
      pageId: pageVersionDto.pageCode,
      ComponentCode: item.moduleId,
      type: item.moduleType,
      activityId: item.connectedActivitiesId,
      comp_style: JSON.parse(item.moduleJson).visual?.visualData[0]?.typeName,
      index: "",
    });
  };
  // 视频
  const videoInit = (item) => {
    track(item);
    // 监听视图范围
    const handleIntersection = (entries) => {
      console.log("这个监听有进来不======", entries);
      entries.forEach((entry) => {
        const videoElement = entry.target;
        if (entry.isIntersecting) {
          videoElement.play();
          console.log("视频播放了");
        } else {
          videoElement.pause();
          console.log("视频停止了");
        }
      });
    };
    // 创建 IntersectionObserver
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1, // 视频至少有 10% 在视图范围内时触发
    });
    // 遍历所有视频元素，添加到观察器中
    videoRefs.current.forEach((videoElement) => {
      observer.observe(videoElement);
    });
    return () => {
      observer.disconnect();
    };
  };

  return (
    <div>
      {pageModuleDto && (
        <div
          id="configPage"
          className="configPage"
          style={{
            backgroundImage: `url(${processOssImage(pageVersionDto?.backUrl)})`,
            backgroundSize: "100%",
            backgroundPosition: "top",
            backgroundRepeat: "repeat",
          }}
        >
          {map(pageModuleDto, (item, index) => {
            console.log("item的值=======", item);

            const statusInfo =
              stairsStatusMap?.[item.moduleJsonData?.base?.[0]?.val?.valCode] ||
              {};
            const key = item.moduleType + index;
            item.moduleId = item.moduleId || item.moduleJsonData.moduleId;
            const styles: any = {};

            const picObj = enhancedSafeChainFind(item?.moduleJsonData)?.base(
              "type",
              "visionImg",
            )?.val?.val;
            const prizeStatus = enhancedSafeChainFind(
              item?.moduleJsonData,
            )?.base("type", "reActivity")?.val?.val?.valCode;
            const status = prizeStatusMap?.[prizeStatus];
            let showPic = picObj?.canImg || "";

            // 遍历增加样式
            forEach(item?.moduleJsonData?.style?.styleData, (e) => {
              if (Array.isArray(e.attr)) {
                e.attr.forEach((attrItem, attrIndex) => {
                  styles[underscoreToCamelCase(attrItem)] =
                    (e.val?.[attrIndex]?.toString() ?? "") + (e.unit || "");
                });
              } else {
                styles[underscoreToCamelCase(e.attr)] =
                  (e.val?.toString() ?? "") + (e.unit || "");
              }
            });
            const onclick = (item) => {
              console.log("点击数据", item);
              // track_click(item)
            };
            switch (item?.moduleType) {
              // 文字
              case "base-text":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <div
                      key={key}
                      className="baseText"
                      style={styles}
                      onClick={() => onclick(item)}
                    >
                      {item.moduleJsonData.base[0].val}
                    </div>
                  </LazyRender>
                );
              // 图片
              case "base-image":
                styles.width = "100%";
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <img
                      key={key}
                      src={
                        processOssImage(item.moduleJsonData.base[1].val) || ""
                      }
                      style={styles}
                      onClick={() => onClickImg(item)}
                    />
                  </LazyRender>
                );
              // 领奖组件
              case "busi-prize":
                if (isGuest) {
                  showPic = picObj?.canImg || "";
                } else if (status) {
                  const { canClaim, canNotClaimReason } = status;
                  if (canClaim) {
                    // 可领取：使用B端配置的图片
                    showPic = picObj.canImg;
                  } else {
                    // 不可领取：根据原因使用B端配置的对应图片
                    if (canNotClaimReason === 1) {
                      // 非活动有效期内
                      showPic = picObj.noCanImg;
                    } else if ([2, 3].includes(canNotClaimReason)) {
                      // 已达单uid参与限制（总次数或频率）
                      showPic = picObj.alreadyImg;
                    } else if (canNotClaimReason === 4) {
                      // 不可参与活动（人群条件不满足）
                      showPic = picObj.noJoinImg || picObj.noCanImg;
                    } else if (canNotClaimReason === 5) {
                      // 已达活动参与限制（全局参与频率）
                      showPic = picObj.activityLimitImg || picObj.noCanImg;
                    } else {
                      // 未知原因，使用默认不可领取图
                      showPic = picObj.noCanImg;
                    }
                  }
                }

                // 如果没有图片，不渲染组件
                if (!showPic) {
                  return null;
                }

                styles.width = "100%";
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <img
                      key={key}
                      src={processOssImage(showPic)}
                      style={styles}
                      onClick={() => {
                        if (isGuest) {
                          receivePrizeData(
                            item.moduleJsonData.base[0].val.valCode,
                            item,
                            picObj,
                          );
                          return;
                        }
                        if (!status) return;

                        const { canClaim, canNotClaimReason } = status;

                        // 非活动有效期内，点击无效果
                        if (!canClaim && canNotClaimReason === 1) {
                          return;
                        }

                        // 其他情况都调用接口（可领取 或 其他限制状态）
                        // 后端会返回对应的toast文案
                        receivePrizeData(
                          item.moduleJsonData.base[0].val.valCode,
                          item,
                          picObj,
                        );
                      }}
                    />
                  </LazyRender>
                );
              // 翻牌组件
              case "busi-raffle":
                console.log("busi-rafflebusi-raffle", item);
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <Lottery
                      key={key}
                      ref={(ref) => (fangPaiRef.current[index] = ref)}
                      showKeyIdx={index}
                      onShowKeyIdx={(val) => {
                        setShowKeyIdx(val);
                      }}
                      activityInfo={item}
                      pageVersionDto={pageVersionDto || {}}
                      pageModuleDto={pageModuleDto || {}}
                      onGainPrize={(val) => {
                        if (prizeDialogRef.current) {
                          prizeDialogRef.current.onOpen(val);
                        }
                        setContentOverflow(true);
                      }}
                    />
                  </LazyRender>
                );
              // 打榜组件
              case "busi-ranking":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <Ranking
                      key={key}
                      activityInfo={item}
                      moduleJsonData={item.moduleJsonData}
                      navHeight={navHeight}
                    />
                  </LazyRender>
                );
                break;
              case "busi-stairs":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <Stairs
                      key={key}
                      stairInfo={item}
                      pageVersionDto={pageVersionDto || {}}
                      pageModuleDto={pageModuleDto || {}}
                      valCode={item.moduleJsonData.base[0].val.valCode}
                      initialStatusInfo={statusInfo || {}}
                      getDetail={getDetail}
                    />
                  </LazyRender>
                );
              // 悬浮窗
              case "base-pop": {
                // 每个悬浮窗只使用自己的图片，避免多个悬浮窗共享同一份数据
                const popBase = item?.moduleJsonData?.base;
                return (
                  // <LazyRender key={key} onInitEvent={() => track(item)}>
                  <FloatingButton
                    key={key}
                    initialPosition={{ right: 30, bottom: 160 }}
                    backgroundImage={popBase?.[1]?.val}
                    style={{
                      width: "112rem",
                      height: "112rem",
                    }}
                    draggable={true}
                    link={popBase?.[0]?.val}
                    onDragEnd={(pos) => console.log("当前位置:", pos)}
                    activityInfo={item}
                  />
                  // </LazyRender>
                );
              }
              // 轮播图
              case "swiper-banner": {
                // 每个轮播模块只使用自己的图片，避免多个轮播器共享同一份数据
                const swiperVal = item?.moduleJsonData?.base?.[0]?.val;
                const swiperImgs = map(swiperVal?.imgUrl, (img, imgIndex) => ({
                  key: `${key}-${imgIndex}`,
                  src: img,
                  link: swiperVal?.imgHref?.[imgIndex],
                  jumpUrlMap: swiperVal?.imgHref?.[imgIndex]?.jumpUrlMap,
                }));
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <ImgSwiper
                      key={key}
                      imgs={swiperImgs}
                      activityInfo={item}
                    />
                  </LazyRender>
                );
              }
              // 金刚区
              case "grid-menu": {
                const menuVision = item.moduleJsonData?.menuVision;
                const baseStyle = item.moduleJsonData?.baseStyle;
                const oneDimensionalArray =
                  menuVision?.tabArr?.reduce(
                    (acc, val) => acc.concat(val),
                    [],
                  ) || [];
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <div className="KingKongArea">
                      <KingKongArea
                        key={key}
                        items={oneDimensionalArray}
                        rows={menuVision?.rowVal || 0}
                        cols={menuVision?.rowNumber || 0}
                        bgImg={menuVision?.bgImg || ""}
                        activityInfo={item}
                        padding={{
                          top: baseStyle?.paddingTop || 0,
                          bottom: baseStyle?.paddingBottom || 0,
                          left: baseStyle?.paddingLeft || 0,
                          right: baseStyle?.paddingRight || 0,
                        }}
                      />
                    </div>
                  </LazyRender>
                );
              }
              case "base-video":
                return (
                  <LazyRender key={key} onInitEvent={() => videoInit(item)}>
                    <Suspense fallback={<div>加载中...</div>}>
                      <VideoPlayer
                        options={{
                          poster: item.moduleJsonData?.base[1]?.val || "",
                          sources: [
                            {
                              // src: videoSource,
                              src: item.moduleJsonData?.base[0]?.val || "",
                              type: "video/mp4",
                            },
                          ],
                        }}
                      />
                    </Suspense>
                  </LazyRender>
                );
              // 动态充电站列表
              case "dynamicStation":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <DynamicStation activityInfo={item} navHeight={navHeight} />
                  </LazyRender>
                );
              // 固定充电站列表
              case "fixedStation":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <FixedStation
                      key={key}
                      activityInfo={item}
                      className={
                        styles[
                          underscoreToCamelCase(
                            item.moduleJsonData?.style?.className,
                          )
                        ] || ""
                      }
                    />
                  </LazyRender>
                );

              // 倒计时组件
              case "base-countdown":
                return (
                  <LazyRender key={key} onInitEvent={() => track(item)}>
                    <BaseCountdown
                      key={key}
                      moduleJsonData={item.moduleJsonData}
                    />
                  </LazyRender>
                );

              default:
                if (props.customCompRender) {
                  return props.customCompRender(item, pageModuleDto);
                }
                break;
            }
          })}
        </div>
      )}

      {/*  中奖弹窗 */}
      <PrizeDialog
        ref={prizeDialogRef}
        onClose={() => {
          if (showKeyIdx != -1) {
            fangPaiRef.current[showKeyIdx]?.closePrize?.();
          }
          setContentOverflow(false);
        }}
      />

      {/*  助力成功弹窗 */}
      <HelpDialog ref={helpDialogRef} />

      {/* 领奖成功弹窗 */}
      <PrizeSuccessPopup ref={prizeSuccessPopupRef} />
    </div>
  );
};

export default Page;
